import { useState, useEffect } from "react";
import { X, Plus, Trash2, ChevronDown, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// All available emojis organized by category for the picker
const EMOJI_CATEGORIES = {
  "Smileys": ["😊", "😀", "😄", "😁", "😃", "🙂", "😉", "😍", "🥰", "😘", "😗", "😋", "😛", "😜", "🤪", "😝", "🤗", "🤭", "🤔", "😏", "😌", "😴", "🥳", "😎", "🤩", "😇"],
  "Gestures": ["👍", "👎", "👌", "✌️", "🤞", "🤟", "🤘", "🤙", "👈", "👉", "👆", "👇", "☝️", "✋", "🤚", "🖐️", "🖖", "👋", "🤝", "🙏", "💪", "👏", "🙌", "👐", "🤲", "🫶"],
  "Hearts": ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟", "♥️"],
  "Symbols": ["⭐", "✨", "💫", "🌟", "⚡", "🔥", "💯", "✅", "❌", "❓", "❗", "💬", "💭", "📝", "🎯", "🎉", "🎊", "🎁", "🏆", "🥇", "🔔", "💡", "⏰", "🚀"],
  "Faces": ["😢", "😭", "😤", "😠", "😡", "🤬", "😱", "😨", "😰", "😥", "😓", "🤯", "😳", "🥺", "😬", "🙄", "😮", "😲", "🥱", "😈", "👿", "💀", "☠️", "🤡"],
};

// Default emoticon shortcuts - word to emoji mappings
// Trigger format: "emoticon [word]" e.g., "emoticon heart" → ❤️
const DEFAULT_EMOJI_SHORTCUTS = [
  { id: "heart", word: "heart", emoji: "❤️" },
  { id: "smile", word: "smile", emoji: "😊" },
  { id: "smiley", word: "smiley", emoji: "😀" },
  { id: "thumbsup", word: "thumbs up", emoji: "👍" },
  { id: "thumbsdown", word: "thumbs down", emoji: "👎" },
  { id: "fire", word: "fire", emoji: "🔥" },
  { id: "star", word: "star", emoji: "⭐" },
  { id: "check", word: "check", emoji: "✅" },
  { id: "x", word: "x", emoji: "❌" },
  { id: "laugh", word: "laugh", emoji: "😄" },
  { id: "cry", word: "cry", emoji: "😢" },
  { id: "clap", word: "clap", emoji: "👏" },
  { id: "party", word: "party", emoji: "🎉" },
  { id: "hundred", word: "hundred", emoji: "💯" },
  { id: "rocket", word: "rocket", emoji: "🚀" },
  { id: "pray", word: "pray", emoji: "🙏" },
  { id: "ok", word: "ok", emoji: "👌" },
  { id: "wave", word: "wave", emoji: "👋" },
  { id: "sparkle", word: "sparkle", emoji: "✨" },
  { id: "love", word: "love", emoji: "💕" },
];

export default function EmojiModal({ isOpen, onClose, onInsertEmoji }) {
  const [emojiShortcuts, setEmojiShortcuts] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newWord, setNewWord] = useState("");
  const [newEmoji, setNewEmoji] = useState("");
  const [showNewEmojiPicker, setShowNewEmojiPicker] = useState(false);
  const [pickerCategory, setPickerCategory] = useState("Smileys");
  
  // For editing existing shortcuts
  const [editingId, setEditingId] = useState(null);
  const [showEditPicker, setShowEditPicker] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadEmojiShortcuts();
    }
  }, [isOpen]);

  const loadEmojiShortcuts = async () => {
    try {
      const response = await axios.get(`${API}/emoji-shortcuts`, { withCredentials: true });
      if (response.data.shortcuts && response.data.shortcuts.length > 0) {
        setEmojiShortcuts(response.data.shortcuts);
      } else {
        setEmojiShortcuts(DEFAULT_EMOJI_SHORTCUTS);
        await saveEmojiShortcuts(DEFAULT_EMOJI_SHORTCUTS);
      }
    } catch (error) {
      setEmojiShortcuts(DEFAULT_EMOJI_SHORTCUTS);
    }
  };

  const saveEmojiShortcuts = async (shortcuts) => {
    try {
      await axios.post(`${API}/emoji-shortcuts`, { shortcuts }, { withCredentials: true });
    } catch (error) {
      console.error("Failed to save emoji shortcuts:", error);
    }
  };

  const handleChangeEmoji = async (id, newEmoji) => {
    const updated = emojiShortcuts.map(s => 
      s.id === id ? { ...s, emoji: newEmoji } : s
    );
    setEmojiShortcuts(updated);
    await saveEmojiShortcuts(updated);
    setEditingId(null);
    setShowEditPicker(false);
  };

  const handleDelete = async (id) => {
    const updated = emojiShortcuts.filter(s => s.id !== id);
    setEmojiShortcuts(updated);
    await saveEmojiShortcuts(updated);
    toast.success("Shortcut deleted");
  };

  const handleAddNew = async () => {
    if (!newWord.trim()) {
      toast.error("Please enter a word");
      return;
    }
    if (!newEmoji) {
      toast.error("Please select an emoji");
      return;
    }

    // Check if word already exists
    if (emojiShortcuts.some(s => s.word.toLowerCase() === newWord.toLowerCase().trim())) {
      toast.error("This word already has a shortcut");
      return;
    }

    const newShortcut = {
      id: Date.now().toString(),
      word: newWord.toLowerCase().trim(),
      emoji: newEmoji
    };

    const updated = [...emojiShortcuts, newShortcut];
    setEmojiShortcuts(updated);
    await saveEmojiShortcuts(updated);
    
    setNewWord("");
    setNewEmoji("");
    setShowAddNew(false);
    toast.success("Shortcut added!");
  };

  const resetToDefaults = async () => {
    setEmojiShortcuts(DEFAULT_EMOJI_SHORTCUTS);
    await saveEmojiShortcuts(DEFAULT_EMOJI_SHORTCUTS);
    toast.success("Reset to defaults");
  };

  // Emoji Picker Component
  const EmojiPicker = ({ onSelect, selectedEmoji }) => (
    <div className="p-3 rounded-xl" style={{ backgroundColor: '#0f1419', border: '1px solid #f59e0b' }}>
      {/* Category Tabs */}
      <div className="flex flex-wrap gap-1 mb-3">
        {Object.keys(EMOJI_CATEGORIES).map(cat => (
          <button
            key={cat}
            onClick={() => setPickerCategory(cat)}
            className="px-2 py-1 rounded text-xs transition-colors"
            style={{ 
              backgroundColor: pickerCategory === cat ? '#f59e0b' : '#2d3748',
              color: pickerCategory === cat ? '#000' : '#a0aec0'
            }}
          >
            {cat}
          </button>
        ))}
      </div>
      {/* Emoji Grid */}
      <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
        {EMOJI_CATEGORIES[pickerCategory].map((emoji, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(emoji)}
            className={`w-8 h-8 flex items-center justify-center rounded text-lg hover:bg-gray-700 transition-colors ${selectedEmoji === emoji ? 'ring-2 ring-yellow-500' : ''}`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid #2d3748' }}>
          <div className="flex items-center gap-2">
            <span className="text-xl">😊</span>
            <h2 className="text-lg font-bold">Emoticon Shortcuts</h2>
            <button onClick={() => setShowHelp(!showHelp)} className="p-1 rounded-full hover:bg-gray-700">
              <HelpCircle className="w-4 h-4" style={{ color: '#718096' }} />
            </button>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Help Section */}
        {showHelp && (
          <div className="p-4 text-sm" style={{ backgroundColor: '#0f1419', borderBottom: '1px solid #2d3748' }}>
            <h3 className="font-bold mb-2" style={{ color: '#f59e0b' }}>How to Use:</h3>
            <p className="mb-2" style={{ color: '#a0aec0' }}>
              Say <strong>"emoticon [word]"</strong> while recording to insert an emoji.
            </p>
            <div className="space-y-1" style={{ color: '#718096' }}>
              <p>• "emoticon heart" → ❤️</p>
              <p>• "emoticon thumbs up" → 👍</p>
              <p>• "emoticon fire" → 🔥</p>
            </div>
            <p className="mt-2 text-xs" style={{ color: '#718096' }}>
              Click the emoji dropdown to change it, or add your own words below.
            </p>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto p-4" style={{ maxHeight: 'calc(85vh - 140px)' }}>
          {/* Action Buttons */}
          <div className="flex justify-between mb-4">
            <Button 
              onClick={() => setShowAddNew(!showAddNew)} 
              size="sm" 
              className="flex items-center gap-2" 
              style={{ backgroundColor: '#f59e0b', color: '#000' }}
            >
              <Plus className="w-4 h-4" /> Add New Word
            </Button>
            <Button 
              onClick={resetToDefaults} 
              size="sm" 
              variant="outline" 
              className="text-xs" 
              style={{ borderColor: '#2d3748', color: '#718096' }}
            >
              Reset to Defaults
            </Button>
          </div>

          {/* Add New Form */}
          {showAddNew && (
            <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}>
              <h3 className="font-medium mb-3" style={{ color: '#f59e0b' }}>Add New Emoticon Shortcut</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color: '#718096' }}>
                    Word (what you say after "emoticon")
                  </label>
                  <input
                    type="text"
                    value={newWord}
                    onChange={(e) => setNewWord(e.target.value)}
                    placeholder="e.g., wink, cool, sad"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ backgroundColor: '#1a2332', color: '#f5f5f5', border: '1px solid #2d3748' }}
                  />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color: '#718096' }}>
                    Choose Emoji
                  </label>
                  <button
                    onClick={() => setShowNewEmojiPicker(!showNewEmojiPicker)}
                    className="w-full px-3 py-2 rounded-lg text-left flex items-center justify-between"
                    style={{ backgroundColor: '#1a2332', color: '#f5f5f5', border: '1px solid #2d3748' }}
                  >
                    <span>{newEmoji || "Select an emoji..."}</span>
                    <ChevronDown className="w-4 h-4" style={{ color: '#718096' }} />
                  </button>
                  {showNewEmojiPicker && (
                    <div className="mt-2">
                      <EmojiPicker 
                        onSelect={(emoji) => { setNewEmoji(emoji); setShowNewEmojiPicker(false); }} 
                        selectedEmoji={newEmoji}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAddNew} size="sm" style={{ backgroundColor: '#22c55e' }}>
                    Add Shortcut
                  </Button>
                  <Button onClick={() => { setShowAddNew(false); setNewWord(""); setNewEmoji(""); }} size="sm" variant="outline" style={{ borderColor: '#2d3748' }}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Shortcuts List */}
          <div className="space-y-2">
            <p className="text-xs mb-2" style={{ color: '#718096' }}>
              Say "emoticon [word]" to insert the emoji. Click the emoji to change it.
            </p>
            {emojiShortcuts.map((shortcut) => (
              <div 
                key={shortcut.id} 
                className="flex items-center justify-between p-3 rounded-xl"
                style={{ backgroundColor: '#0f1419', border: '1px solid #2d3748' }}
              >
                <div className="flex items-center gap-3">
                  {/* Emoji with dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => {
                        setEditingId(editingId === shortcut.id ? null : shortcut.id);
                        setShowEditPicker(editingId !== shortcut.id);
                      }}
                      className="w-10 h-10 flex items-center justify-center rounded-lg text-2xl hover:bg-gray-700 transition-colors"
                      style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}
                    >
                      {shortcut.emoji}
                    </button>
                    {editingId === shortcut.id && showEditPicker && (
                      <div className="absolute top-full left-0 mt-2 z-10 w-64">
                        <EmojiPicker 
                          onSelect={(emoji) => handleChangeEmoji(shortcut.id, emoji)} 
                          selectedEmoji={shortcut.emoji}
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Word */}
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#f5f5f5' }}>
                      "{shortcut.word}"
                    </p>
                    <p className="text-xs" style={{ color: '#718096' }}>
                      emoticon {shortcut.word} → {shortcut.emoji}
                    </p>
                  </div>
                </div>

                {/* Delete button */}
                <button 
                  onClick={() => handleDelete(shortcut.id)}
                  className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" style={{ color: '#ef4444' }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
